import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { useDefaultSearchModel } from '#pplx/frontend/apps/ask/search/hooks/useDefaultSearchModel.js';
import { useSearchModels } from '#pplx/frontend/ask-models/hooks/useSearchModels.js';
import { SearchModel } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
/**
 * Handles switching away from text-only models when images are attached.
 *
 * @returns Nothing; requests model changes through onModelSwitch and shows a warning toast.
 */
export const useTextOnlyModelHandler = ({ hasImageAttachments, selectedModel, onModelSwitch, }) => {
    const { $t } = useIntl();
    const { hasAccessToProFeatures } = useIsSubscribed();
    const { openToast } = useToastContext();
    const { getModelConfig, getModelLabel, isLoaded: isSearchModelsLoaded, } = useSearchModels({
        reason: 'use_text_only_model_handler',
    });
    const lastSwitchedFromModelRef = useRef(null);
    const defaultSearchModel = useDefaultSearchModel();
    useEffect(() => {
        if (!isSearchModelsLoaded) {
            return;
        }
        const modelConfig = getModelConfig(selectedModel);
        const textOnlyModel = modelConfig?.text_only_model === true;
        // Reset tracking when images are removed or model supports images
        if (!hasImageAttachments || !textOnlyModel) {
            lastSwitchedFromModelRef.current = null;
            return;
        }
        // Don't trigger switch if we've already switched from this model
        if (lastSwitchedFromModelRef.current === selectedModel) {
            return;
        }
        const message = $t({
            defaultMessage: '{modelName} does not support images. Switched to the default best model.',
            description: 'Warning toast shown when a text-only model is automatically switched because the user attached images',
            id: 'acc2926e15',
        }, {
            modelName: getModelLabel(selectedModel) ??
                $t({
                    defaultMessage: 'Selected model',
                    description: 'Fallback label used for the model name when the configured model label is unavailable',
                    id: 'de323a4674',
                }),
        });
        // If the current model doesn't support images and user has image attachments, switch to best model
        // Schedule the state update to avoid updating during render
        openToast({
            message,
            variant: 'warning',
            timeoutSeconds: null,
        });
        lastSwitchedFromModelRef.current = selectedModel;
        // Part of dynamic model config migration
        onModelSwitch(defaultSearchModel);
    }, [
        hasImageAttachments,
        selectedModel,
        isSearchModelsLoaded,
        getModelConfig,
        openToast,
        $t,
        onModelSwitch,
        hasAccessToProFeatures,
        defaultSearchModel,
        getModelLabel,
    ]);
};
//# sourceMappingURL=useTextOnlyModelHandler.js.map